import React, { useState, useEffect, createContext, useContext } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type AuthContextType = {
  currentUser: User | null;
  isLoading: boolean;
  handleSignOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error("Visibility check error:", error);
          return;
        }
        if (data.session?.user?.id !== currentUser?.id) {
          setCurrentUser(data.session?.user ?? null);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [currentUser]);

  useEffect(() => {
    const checkUser = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error("Initial session fetch error:", error);
        return;
      }
      if (data.session?.user?.id !== currentUser?.id) {
        setCurrentUser(data.session?.user ?? null);
      }
    };

    let debounceTimeout: any;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth state changed:", event);

      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        if (session?.user?.id !== currentUser?.id) {
          setCurrentUser(session?.user ?? null);
        }

        if (event === "SIGNED_OUT") {
          window.location.href = "/auth";
        }
      }, 100);
    });

    checkUser();

    return () => {
      subscription.unsubscribe();
      clearTimeout(debounceTimeout);
    };
  }, [currentUser]);

  const handleSignOut = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setCurrentUser(null);
      toast.success("Successfully signed out!");
      window.location.href = "/";
    } catch (error: any) {
      console.error("Sign out error:", error);
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ currentUser, isLoading, handleSignOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
